import { fromTokens, TokensUi } from '@/common/primary/token/Tokens.ui';
import { ClubState } from '@/fairplayer/domain/club/ClubState';
import { Exclusive } from '@/fairplayer/domain/exclusive/Exclusive';
import { ExclusiveCategory } from '@/fairplayer/domain/exclusive/ExclusiveCategory';
import { LocalDate } from '@/common/domain/LocalDate';
import { Club } from '@/fairplayer/domain/club/Club';
import { fromMedia, MediaUi } from '@/fairplayer/primary/marketplace/exclusives-list/Media.ui';
import { FanId } from '@/common/domain/FanId';
import { MarkdownUi } from '@/common/primary/Markdown.ui';
import { renderMarkdown } from '@/common/primary/MarkdownRenderer';
import { AuctionLotUi, fromAuctionLot } from '@/fairplayer/primary/marketplace/AuctionLot.ui';
import { ExclusiveTaxDeductionUi, fromExclusiveTaxDeduction } from '@/fairplayer/domain/exclusive/ExclusiveTaxDeduction.ui';

type CategoryIcon = 'fp-glyph-handshake' | 'fp-glyph-award';

export interface ExclusiveUi {
  id: string;
  name: string;
  description: MarkdownUi;
  slug: string;
  imageUrl: string;
  medias: MediaUi[];
  pricing: TokensUi;
  visible: boolean;
  stock: number;
  isOutOfStock: boolean;
  category: ExclusiveCategory;
  categoryIcon: CategoryIcon;
  isUpcoming: boolean;
  isLiked: boolean;
  upcomingDate?: LocalDate;
  auctionLot?: AuctionLotUi;
  taxDeduction?: ExclusiveTaxDeductionUi;
}

const toCategoryIcon = (category: ExclusiveCategory): CategoryIcon => {
  switch (category) {
    case ExclusiveCategory.EXPERIENCE:
      return 'fp-glyph-handshake';
    case ExclusiveCategory.COLLECTIBLE:
      return 'fp-glyph-award';
  }
};

const computeStock = (stock: number, clubState: ClubState) => {
  if (clubState === ClubState.STAND_BY) {
    return 0;
  }

  return stock;
};

const computeVisible = (visible: boolean, clubState: ClubState) => {
  if (clubState === ClubState.ARCHIVED) {
    return false;
  }

  return visible;
};

export const fromExclusive = (exclusive: Exclusive, club: Club, fanId?: FanId, isLiked?: boolean): ExclusiveUi => {
  const stock = computeStock(exclusive.stock, club.state);
  const visible = computeVisible(exclusive.visible, club.state);

  return {
    id: exclusive.id,
    name: exclusive.name,
    description: renderMarkdown(exclusive.description),
    imageUrl: exclusive.imageUrl,
    medias: exclusive.medias.map(fromMedia),
    pricing: fromTokens(exclusive.pricing),
    visible,
    stock,
    isOutOfStock: stock === 0,
    category: exclusive.category,
    categoryIcon: toCategoryIcon(exclusive.category),
    slug: exclusive.slug,
    isUpcoming: club.state === ClubState.UPCOMING,
    isLiked: isLiked ?? false,
    upcomingDate: club.upcomingDate.orElseUndefined(),
    auctionLot: exclusive.auctionLot.map(lot => fromAuctionLot(lot, fanId)).orElseUndefined(),
    taxDeduction: exclusive.taxDeduction.map(fromExclusiveTaxDeduction).orElseUndefined(),
  };
};
